import { useEffect, useMemo, useState } from 'react'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { useAuth } from '../providers/AuthProvider'
import { resolveApiBaseUrl } from '../config/api-base-url'
import { extractErrorMessage, normalizeBaseUrl, parseResponsePayload } from '../utils/apiPayload'

type MatrixStatus = 'idle' | 'loading' | 'ready' | 'empty' | 'error'

interface AttendanceLessonCell {
  lessonId: string
  lessonNo: number
  mark: string | null
  markLabel: string
  makeupRequestId: string | null
  makeupStatus: string | null
}

interface AttendanceStudentRow {
  id: string
  fullName: string
  phone: string | null
  flowNo: string
  openMakeupId: string | null
  cells: AttendanceLessonCell[]
}

interface AttendanceLesson {
  id: string
  lessonNo: number
  date: string | null
  label: string
}

interface AttendanceMatrixPayload {
  flowNo: string
  availableFlows: string[]
  generatedAt: string
  lessons: AttendanceLesson[]
  students: AttendanceStudentRow[]
}

const defaultApiBaseUrl = resolveApiBaseUrl()

export const AttendanceMatrix = () => {
  const auth = useAuth()
  const initialFlowNo = auth.session?.user.flowNo?.trim() || '171'

  const [flowInput, setFlowInput] = useState(initialFlowNo)
  const [activeFlowNo, setActiveFlowNo] = useState(initialFlowNo)
  const [status, setStatus] = useState<MatrixStatus>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [matrix, setMatrix] = useState<AttendanceMatrixPayload | null>(null)

  const baseUrl = useMemo(() => normalizeBaseUrl(defaultApiBaseUrl), [])

  useEffect(() => {
    if (auth.status !== 'authenticated' || !auth.session) {
      setStatus('error')
      setErrorMessage('Матрица недоступна: отсутствует активная сессия преподавателя.')
      return
    }

    let isCancelled = false

    const authHeaders: Record<string, string> = {
      Authorization: `Bearer ${auth.session.token}`,
    }

    const requestMatrix = async () => {
      const query = new URLSearchParams({ flowNo: activeFlowNo })
      return fetch(`${baseUrl}/ops/console/attendance/matrix?${query.toString()}`, {
        method: 'GET',
        headers: authHeaders,
      })
    }

    const load = async () => {
      setStatus('loading')
      setErrorMessage(null)

      try {
        let response = await requestMatrix()

        if (response.status === 404) {
          await fetch(`${baseUrl}/ops/console/attendance/fixture`, {
            method: 'POST',
            headers: {
              ...authHeaders,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ flowNo: activeFlowNo }),
          })
          response = await requestMatrix()
        }

        const payload = await parseResponsePayload(response)
        if (isCancelled) {
          return
        }

        if (!response.ok) {
          setStatus('error')
          setErrorMessage(
            `Не удалось загрузить матрицу (${response.status}): ${extractErrorMessage(payload, 'Неизвестная ошибка')}`,
          )
          return
        }

        if (!payload || typeof payload !== 'object') {
          setStatus('error')
          setErrorMessage('API вернул пустой или некорректный payload матрицы.')
          return
        }

        const matrixPayload = payload as AttendanceMatrixPayload
        setMatrix(matrixPayload)

        if (!matrixPayload.students?.length || !matrixPayload.lessons?.length) {
          setStatus('empty')
          return
        }

        setStatus('ready')
      } catch (error) {
        if (isCancelled) {
          return
        }

        setStatus('error')
        setErrorMessage(error instanceof Error ? error.message : String(error))
      }
    }

    void load()

    return () => {
      isCancelled = true
    }
  }, [activeFlowNo, auth.session, auth.status, baseUrl])

  const availableFlows = matrix?.availableFlows?.length ? matrix.availableFlows : ['171', '172', '173', '174', '175']

  return (
    <>
      <h2 className="content-title">Посещаемость</h2>
      <p className="hint">Матрица отметок по потокам и урокам. + — присутствовал, Н — не был, О — отработка.</p>

      <form
        className="matrix-controls"
        onSubmit={(event) => {
          event.preventDefault()
          const normalized = flowInput.trim() || '171'
          setFlowInput(normalized)
          setActiveFlowNo(normalized)
        }}
      >
        <label htmlFor="flowNo">Поток</label>
        <input
          id="flowNo"
          value={flowInput}
          onChange={(event) => setFlowInput(event.target.value)}
          placeholder="171"
        />
        <button type="submit">Загрузить</button>
      </form>

      <div className="matrix-flow-list" role="list" aria-label="Быстрый выбор потока">
        {availableFlows.map((flowNo) => (
          <button
            type="button"
            key={flowNo}
            className={flowNo === activeFlowNo ? 'active' : undefined}
            onClick={() => {
              setFlowInput(flowNo)
              setActiveFlowNo(flowNo)
            }}
          >
            {flowNo}
          </button>
        ))}
      </div>

      {status === 'loading' ? <LoadingSpinner label="Загружаем матрицу посещаемости..." /> : null}

      {status === 'error' ? <div className="alert">{errorMessage ?? 'Неизвестная ошибка матрицы'}</div> : null}

      {status === 'empty' && matrix ? (
        <p className="hint">Для потока {matrix.flowNo} пока нет данных матрицы. Попробуйте другой поток.</p>
      ) : null}

      {status === 'ready' && matrix ? (
        <>
          <p className="hint">
            Поток {matrix.flowNo} • учеников: {matrix.students.length} • уроков: {matrix.lessons.length} • обновлено: {' '}
            {new Date(matrix.generatedAt).toLocaleString('ru-RU')}
          </p>

          <div className="matrix-scroll">
            <table className="matrix-table">
              <thead>
                <tr>
                  <th>Ученик</th>
                  {matrix.lessons.map((lesson) => (
                    <th key={lesson.id}>У{lesson.lessonNo}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrix.students.map((student) => (
                  <tr key={student.id}>
                    <th>
                      <span className="matrix-student-name">{student.fullName}</span>
                      <span className="matrix-student-phone">{student.phone ?? 'телефон не указан'}</span>
                    </th>
                    {student.cells.map((cell) => (
                      <td key={cell.lessonId}>
                        <span className={`matrix-mark ${cell.makeupRequestId ? 'has-makeup' : ''}`}>{cell.markLabel}</span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </>
  )
}
